import * as R from 'ramda';
import type { PartialDeep } from 'type-fest';
import Log from '../common/logging';
import {
  Configuration,
  defaultConfiguration,
  documentConfig,
  ExternalLinks,
  RouteConfig,
  ServiceConfig,
  SIPServiceConfig,
} from './defaultConfig';
import stageConfig from './stageConfig';
import chainConfig from './chainConfig';
import {
  Action,
  ActionConfig,
  CaptchaProvider,
  FlowConfig,
  UserFlow,
  ClientFlowConfig,
  ClientFlowConfigs,
  Provider,
  ProviderConfig,
  ProviderConfigs,
  SipConfig,
  networkMapping,
} from './networkConfig';
import loadDynamicConfig from './dynamicConfig';
import { Routes } from './routes';
import updatedDefaultNetworkConfigsWithProdIssuer from './configUtils';
import { ClientFlowType } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceData';
import chainStageConfig from './chainStageConfig';

export type OwnerSignsConfigResult = {
  isValid: boolean;
  errorRoute: Routes | null;
};

const validOwnerSignsConfig: OwnerSignsConfigResult = {
  isValid: true,
  errorRoute: null,
};

interface ConfigResolver {
  /** Returns service configurations for the current environment */
  getServiceConfig(): ServiceConfig;

  /** Returns route configurations for the current environment */
  getRouteConfig(): RouteConfig;

  /**
   * Check if the given network matches a network address.
   * If it matches, return the relevant config alias
   *
   * @param network - The given network
   * @returns - the mapped network alias, or the network as given
   */
  transformFlowType(flowType: ClientFlowType): UserFlow;

  /**
   * Transform the incoming provider parameter according to the transformation rules
   * 1. If the provider is one of the captcha providers, then set the provider as Captcha
   * 2. If the provider is MARKETING, then return undefined so we go through the same flow as for issuance
   * 3. If the provider is a known provider, return the result, otherwise return undefined
   * @param provider - the incoming provider value
   */
  transformIncomingProvider(provider: string): Provider | undefined;

  /**
   * Validate the given owner signs value in context of the network config
   * @param network
   * @param action
   * @param ownerSignsParameter
   * @param chain
   * @param civicSendsDisabledQueryParam
   * @returns - An owner signs is valid result with an optional error screen to show
   */
  validateOwnerSignsConfig(
    network: string | undefined,
    action: Action | undefined,
    ownerSignsParameter: string | undefined,
    gatekeeperSendsTransaction: boolean,
    chain: string | undefined,
    networkSupportsCivicSends: boolean,
  ): OwnerSignsConfigResult;

  /**
   * Return the route for a given network, action and optional provider
   *
   * @param clientFlowType - the given flow type
   * @param action - the given action
   * @param provider? - the optional provider
   */
  getRoute(clientFlowType: ClientFlowType | undefined, action: Action, provider?: Provider, chain?: string): string;

  /**
   * Return the flow config required for a given network and action
   *
   * @param clientFlowType - the given flow type
   * @param action - the given action
   * @returns the FlowConfig for the network and action, or null if no config found
   */
  getFlowConfig(clientFlowType: ClientFlowType, action: Action, chain?: string): FlowConfig | null;

  /**
   * Load the external links for a given network
   *
   * @returns the external links from the config
   */
  getExternalLinksConfig(): ExternalLinks;

  /**
   * Load all provider configurations for a given network and action
   */
  getProviderConfigurations(network: string, action: Action): ProviderConfigs;

  /**
   * Get provider config for the current network, action and selected provider
   */
  getProviderConfig<T extends ProviderConfig>(network: string, action: Action, provider: Provider): T;

  /**
   * Return whether proof of wallet ownership flag is set and whether it is required
   *
   * @param network - the given network
   * @param action - the given action
   * @param chain? - the optional chain
   */
  flowRequiresLegacyPOWO(network: string, action: Action, chain?: string): boolean | undefined;
}

const ownerSignsParameterCheckExcludedActions = [Action.LINK, Action.STATUS];

class HardcodedConfigResolver implements ConfigResolver {
  config: Configuration;

  /** Load stage specific config from the typed config files in ./stageConfig */
  static loadStageConfig(): PartialDeep<Configuration> {
    const stage = (process.env.REACT_APP_STAGE || 'dev').toLowerCase();
    const config = stageConfig[stage] || {};
    Log.debug(`Loaded stage specific config for ${stage}`, config);
    return config;
  }

  static loadChainConfig(chain?: string): PartialDeep<Configuration> {
    const config = chain ? chainConfig[chain] || {} : {};
    Log.debug(`Loaded chain specific config for ${chain}`, config);
    return config;
  }

  static loadChainStageConfig(chain: string): PartialDeep<Configuration> {
    const stage = (process.env.REACT_APP_STAGE || 'dev').toLowerCase();
    const config = chain ? chainStageConfig[chain]?.[stage] || {} : {};
    Log.debug(`Loaded chain stage specific config for ${chain}`, config);
    return config;
  }

  /** Load the dynamic config data specific in the json file ./dynamicConfig/dynamicConfig.json */
  static loadDynamicConfig(): PartialDeep<Configuration> {
    const config = loadDynamicConfig();
    Log.debug('Loaded dynamic config', config);
    return config;
  }

  /** Merge the default config with the stage specific config and the dynamic config */
  static loadConfig(chain?: string): Configuration {
    // the default configuration can be overwritten by
    // configurations defined for the running stage
    let loadedStageConfig = HardcodedConfigResolver.loadStageConfig();
    if (chain) {
      const loadedChainConfig = HardcodedConfigResolver.loadChainConfig(chain);
      loadedStageConfig = R.mergeDeepRight(loadedStageConfig, loadedChainConfig);
      const loadedChainStageConfig = HardcodedConfigResolver.loadChainStageConfig(chain);
      loadedStageConfig = R.mergeDeepRight(loadedStageConfig, loadedChainStageConfig);
    }

    const loadedDynamicConfig = HardcodedConfigResolver.loadDynamicConfig();
    const mergedConfig = R.mergeDeepRight(defaultConfiguration, loadedStageConfig);
    const result = R.mergeDeepRight(mergedConfig, loadedDynamicConfig);
    return result as Configuration;
  }

  constructor(config?: Configuration) {
    if (!config) {
      this.config = HardcodedConfigResolver.loadConfig();
    } else {
      this.config = config;
    }
  }

  getServiceConfig(): ServiceConfig {
    return this.config.services;
  }

  getRouteConfig(): RouteConfig {
    return this.config.routeConfigs;
  }

  getExternalLinksConfig(): ExternalLinks {
    return this.config.externalLinks;
  }

  transformFlowType(flowType: ClientFlowType): UserFlow {
    return networkMapping[flowType];
  }

  transformIncomingProvider(provider: string): Provider | undefined {
    const isCaptchaProvider = R.includes(provider, Object.values(CaptchaProvider));
    if (isCaptchaProvider) {
      return Provider.CAPTCHA;
    }

    if (provider === Provider.MARKETING) {
      return undefined;
    }

    if (R.includes(provider, Object.values(Provider))) {
      return provider as Provider;
    }

    return undefined;
  }

  private validateOwnerSignsWithNetworkConfig(
    civicSendsDisabled: boolean,
    gatekeeperSendsTransaction: boolean,
  ): OwnerSignsConfigResult {
    const ownerSignsEnabled = !gatekeeperSendsTransaction;
    // if the owner signs or civic sends is not disabled, then config is valid
    // config is invalid if the inputs desire a gatekeeper-send but it is disabled
    const isValid = ownerSignsEnabled || !civicSendsDisabled;
    return {
      isValid,
      errorRoute: !isValid ? Routes.CIVIC_SIGNS_NOT_SUPPORTED : null,
    };
  }

  validateOwnerSignsConfig(
    network: string | undefined,
    action: Action | undefined,
    ownerSignsParameter: string | undefined,
    gatekeeperSendsTransaction: boolean,
    chain: string | undefined,
    networkSupportsCivicSends: boolean,
  ): OwnerSignsConfigResult {
    if (ownerSignsParameter && networkSupportsCivicSends) {
      Log.debug(`Civic sends for chain ${chain} not disabled`);
      return validOwnerSignsConfig;
    }
    // return unsupported version if owner signs parameter not provided and the action is not excluded
    if (ownerSignsParameter === undefined && !R.contains(action, ownerSignsParameterCheckExcludedActions)) {
      return { isValid: false, errorRoute: Routes.UNSUPPORTED_VERSION };
    }

    // if no network is provided, or if the action is excluded from the check, then default to valid=true
    if (!network || R.contains(action, ownerSignsParameterCheckExcludedActions)) {
      return validOwnerSignsConfig;
    }

    return this.validateOwnerSignsWithNetworkConfig(!networkSupportsCivicSends, gatekeeperSendsTransaction);
  }

  getRoute(clientFlowType: ClientFlowType | undefined, action: Action, provider?: Provider, chain?: string): string {
    // default to route name based on action name
    const defaultConfig = {
      route: `/${action}`,
    };
    // Revocation doesn't require a GKNS and flowType, so simply reroute to the revocation route
    if (action === Action.REVOKE) {
      const defaultRoute = '/revocation';
      return defaultRoute;
    }
    if (clientFlowType === undefined) {
      const { route: defaultRoute } = defaultConfig;
      return defaultRoute;
    }
    const userFlowType = this.transformFlowType(clientFlowType);
    const networkConfig = this.getNetworkConfig(userFlowType);
    const { route: defaultRoute } = R.pathOr(defaultConfig, [action], networkConfig);
    // return the provider specific route on refresh, if available
    const providerConfig =
      provider && action === Action.REFRESH ? this.getProviderConfig(clientFlowType, action, provider, chain) : {};
    return R.has('route', providerConfig) ? (providerConfig.route as string) : defaultRoute;
  }

  getFlowConfig(clientFlowType: ClientFlowType, action: Action, chain?: string): FlowConfig | null {
    const userFlowType = this.transformFlowType(clientFlowType);
    const networkConfig = this.getNetworkConfig(userFlowType);
    const result = R.pathOr({}, [action, 'config'], networkConfig);

    const flowChainSpecificConfig = chain
      ? R.pathOr({}, ['flowConfigs', userFlowType, action, 'config'], HardcodedConfigResolver.loadChainConfig(chain))
      : {};
    return (R.mergeRight(result, flowChainSpecificConfig) as FlowConfig) || null;
  }

  flowRequiresLegacyPOWO(clientFlowType: ClientFlowType, action: Action, chain?: string): boolean | undefined {
    const flowConfig = this.getFlowConfig(clientFlowType, action, chain);
    Log.debug(`flowRequiresLegacyPOWO flowConfig?.legacyPOWOEnabled: ${flowConfig?.legacyPOWOEnabled}`);
    return flowConfig?.legacyPOWOEnabled;
  }

  getNetworkConfig(networkKey: string): ClientFlowConfig {
    // For prod, we have to replace the IDV DID with the prod one.
    const isProd = (process.env.REACT_APP_STAGE || 'dev') === 'prod';
    // The transformation function expects an object keyed by network name. Since there's no network named 'default' we pretend there is when calling the transform.
    const transformedConfig: ClientFlowConfigs = isProd
      ? updatedDefaultNetworkConfigsWithProdIssuer({ defaultNetwork: documentConfig })
      : { defaultNetwork: documentConfig };
    const docConfigToUse: ClientFlowConfig = transformedConfig.defaultNetwork;
    // If we have a network specific config, return it, otherwise return the default of document config
    return R.propOr(docConfigToUse, networkKey, this.config.flowConfigs);
  }

  getProviderConfigurations(clientFlowType: ClientFlowType, action: Action): ProviderConfigs {
    const defaultConfig = {};
    const userFlowType = this.transformFlowType(clientFlowType);
    const networkConfig = this.getNetworkConfig(userFlowType);
    return R.pathOr(defaultConfig, [action, 'providers'], networkConfig);
  }

  getProviderConfig<T extends ProviderConfig>(
    flowType: ClientFlowType,
    action: Action,
    provider: Provider,
    chain?: string,
  ): T {
    const useChain = chain || '';
    const defaultConfig = {};
    const networkKey = this.transformFlowType(flowType);
    const networkConfig = this.getNetworkConfig(networkKey);
    const networkSpecificConfig = R.pathOr(defaultConfig, [action, 'providers', provider], networkConfig) as T;
    const networkChainSpecificConfig = R.pathOr(
      {},
      ['flowConfigs', networkKey, action, 'providers', provider],
      HardcodedConfigResolver.loadChainConfig(useChain),
    ) as T;
    return R.mergeRight(networkSpecificConfig, networkChainSpecificConfig) as T;
  }
}

// instance to use
const Resolver = new HardcodedConfigResolver();

export { Action, Provider, Resolver, HardcodedConfigResolver };
export type {
  ConfigResolver,
  Configuration,
  SipConfig,
  SIPServiceConfig,
  ProviderConfig,
  ProviderConfigs,
  ClientFlowConfigs as NetworkConfigs,
  ActionConfig,
  FlowConfig,
  ServiceConfig,
  RouteConfig,
};
